import React, { useState, useEffect } from 'react';
import { useWallet } from '../contexts/WalletContext';
import NFTCard from './NFTCard';
import correctedNFTData from '../corrected_nft_people_tokens.json';
import { score_data } from '../data.js';

const UserNFTs = () => {
  const { userAddress, isConnected } = useWallet();
  const [userNFTs, setUserNFTs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('rank');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');

  const itemsPerPage = 50;

  // GraphQL query to fetch user's NFT-People
  const fetchUserNFTs = async (walletAddress) => {
    if (!walletAddress) return;

    setLoading(true);
    setError(null);

    try {
      const query = `
        query HenCreatorHeldCount($walletAddress: String!) {
          token_holder(
            where: {holder_address: {_eq: $walletAddress}, quantity: {_gt: "0"}, token: {fa_contract: {_eq: "KT1RJ6PbjHpwc3M5rw5s2Nbmefwbuwbdxton"}, creators: {creator_address: {_eq: "tz1QtdHKG7bNxYvo9KN5xc94BpmQvtYrVqkU"}}}}
          ) {
            token {
              token_id
              name
            }
            last_incremented_at
          }
        }
      `;

      const response = await fetch('https://data.objkt.com/v3/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          variables: { walletAddress }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.errors) {
        throw new Error(`GraphQL error: ${data.errors[0].message}`);
      }

             // Extract token data from the response
       const tokenData = data.data.token_holder.map(holder => ({
         tokenId: holder.token.token_id,
         name: holder.token.name
       }));
       
       // Find corresponding NFTs from our data and enrich with score/rank
       const enrichedUserNFTs = tokenData.map(token => {
         // Find the NFT in our local data
         const nftData = correctedNFTData.find(nft => nft.id.toString() === token.tokenId);
         
         if (nftData) {
           // Find the score data using the API's name field
           const scoreInfo = score_data.find(score => score.Item === token.name);
           
           return {
             ...nftData,
             title: token.name, // Use the API's name field
             score: scoreInfo ? scoreInfo.Score : 0,
             rank: scoreInfo ? scoreInfo.Rank : 9999
           };
         }
         return null;
       }).filter(Boolean); // Remove null entries

      setUserNFTs(enrichedUserNFTs);
      console.log(`✅ Found ${enrichedUserNFTs.length} NFT-People owned by ${walletAddress}`);

    } catch (err) {
      console.error('❌ Error fetching user NFTs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user NFTs when wallet connects
  useEffect(() => {
    if (isConnected && userAddress) {
      fetchUserNFTs(userAddress);
    } else {
      setUserNFTs([]);
    }
  }, [isConnected, userAddress]);

  // Filter and sort user NFTs
  const filteredAndSortedUserNFTs = userNFTs
    .filter(nft => {
      if (searchTerm) {
        return nft.title.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return true;
    })
    .filter(nft => {
      switch (filterBy) {
        case 'top100':
          return nft.rank <= 100;
        case 'top500':
          return nft.rank <= 500;
        case 'rare':
          return nft.rank <= 50;
        default:
          return true;
      }
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'rank':
          aValue = a.rank;
          bValue = b.rank;
          break;
        case 'score':
          aValue = a.score;
          bValue = b.score;
          break;
        case 'name':
          aValue = a.title;
          bValue = b.title;
          break;
        default:
          aValue = a.rank;
          bValue = b.rank;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedUserNFTs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUserNFTs = filteredAndSortedUserNFTs.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  };

  const handleFilterChange = (newFilterBy) => {
    setFilterBy(newFilterBy);
    setCurrentPage(1);
  };

  // Get proper image URL
  const getImageUrl = (nft) => {
    const nftNumber = nft.title?.match(/#(\d+)/)?.[1] || nft.id;
    
    if (nft.display_uri) {
      if (nft.display_uri.startsWith('ipfs://')) {
        const ipfsUrl = nft.display_uri.replace('ipfs://', 'https://ipfs.io/ipfs/');
        return ipfsUrl;
      }
      return nft.display_uri;
    }
    
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="200" fill="#1a1a1a"/>
        <rect width="180" height="180" x="10" y="10" fill="#2a2a2a" rx="15"/>
        <circle cx="100" cy="70" r="25" fill="#4a4a4a"/>
        <text x="100" y="78" font-family="Arial" font-size="12" fill="#888" text-anchor="middle">NFT People</text>
        <text x="100" y="120" font-family="Arial" font-size="28" fill="#fff" text-anchor="middle" font-weight="bold">#${nftNumber}</text>
        <text x="100" y="150" font-family="Arial" font-size="10" fill="#666" text-anchor="middle">No Image</text>
      </svg>
    `)}`;
  };

  if (!isConnected) {
    return (
      <div className="user-nfts-container">
        <div className="wallet-required">
          <div className="wallet-required-icon">🔒</div>
          <h3>Wallet Connection Required</h3>
          <p>Please connect your wallet to view your NFT-People collection.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-nfts-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your NFT-People collection...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-nfts-container">
        <div className="error-state">
          <div className="error-icon">❌</div>
          <h3>Error Loading Collection</h3>
          <p>{error}</p>
          <button 
            onClick={() => fetchUserNFTs(userAddress)}
            className="retry-btn"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-nfts-container">
      {/* Header */}
      {/* <div className="user-nfts-header">
        <h2>My NFT-People Collection</h2>
        <p className="wallet-info">
          Connected: <span className="wallet-address">{userAddress}</span>
        </p>
        <p className="collection-stats">
          Total NFTs: {userNFTs.length} | 
          Showing: {currentUserNFTs.length} of {filteredAndSortedUserNFTs.length}
        </p>
      </div> */}

      {/* Controls */}
      <div className="controls-section">
        <div className="controls-row">
          <div className="controls-left">
            <div className="control-group">
              <input
                type="text"
                placeholder="Search your NFTs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="control-group">
              <span className="control-label">Filter:</span>
              <select 
                value={filterBy} 
                onChange={(e) => handleFilterChange(e.target.value)}
                className="select-dropdown"
              >
                <option value="all">All NFTs</option>
                <option value="top100">Top 100</option>
                <option value="top500">Top 500</option>
                <option value="rare">Rare (Top 50)</option>
              </select>
            </div>
          </div>
          <div className="controls-right">
            <div className="control-group">
              <span className="control-label">Sort by:</span>
              <select 
                value={sortBy} 
                onChange={(e) => handleSortChange(e.target.value)}
                className="select-dropdown"
              >
                <option value="rank">Rarity Rank</option>
                <option value="score">Rarity Score</option>
                <option value="name">Name</option>
              </select>
            </div>
            <button 
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="sort-order-btn"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      {/* NFT Grid */}
      {userNFTs.length === 0 ? (
        <div className="empty-collection">
          <div className="empty-icon">🎭</div>
          <h3>No NFT-People Found</h3>
          <p>You don't own any NFT-People tokens yet.</p>
          <p>Check the main collection to discover and collect some!</p>
        </div>
      ) : (
        <>
          <div className="nft-grid">
            {currentUserNFTs.map((nft) => (
              <NFTCard
                key={nft.id}
                id={nft.id}
                title={nft.title}
                imageUrl={getImageUrl(nft)}
                rank={nft.rank}
                score={nft.score}
                price={null}
                held={true}
                marketplaceUrl={`https://objkt.com/tokens/hicetnunc/${nft.id}`}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2)
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span className="page-btn" style={{ cursor: 'default', opacity: 0.5 }}>...</span>
                    )}
                    <button 
                      onClick={() => handlePageChange(page)}
                      className={`page-btn ${page === currentPage ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}
              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserNFTs;
